// @ts-nocheck
import { Trans, useLingui } from "@lingui/react/macro";
import {
	ActionIcon,
	Button,
	CheckIcon,
	Combobox,
	Group,
	Input,
	InputBase,
	Stack,
	Title,
	useCombobox,
} from "@mantine/core";
import { IconPencil, IconPlus } from "@tabler/icons-react";
import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CrewFields from "@/components/Crew/CrewFields/CrewFields";
import { useCreateCrew } from "@/components/Crew/CrewFields/requests/useCreateCrew";
import { useDeleteCrew } from "@/components/Crew/CrewFields/requests/useDeleteCrew";
import { useUpdateCrewName } from "@/components/Crew/CrewFields/requests/useUpdateCrewName";
import UsageLimit from "@/components/common/UsageLimit/UsageLimit";
import { crewStore, uiStore } from "@/mobx/rootStore";
import { routesList } from "@/routesList";
import { handleApiResponse } from "@/utils/common/api/handleApiResponse";
import { useAppSizeId } from "@/utils/hooks/useAppSizeId";
import classes from "./CrewSelector.module.css";

/**
 * @typedef {Object} CrewSelectorProps
 *
 * @property {"mobile" | "desktop"} variant
 */
/**
 * @param {CrewSelectorProps} props
 */
export default observer(function CrewSelector({ variant = "desktop" }) {
	const navigate = useNavigate();
	const { t } = useLingui();

	const [editingCrew, setEditingCrew] = useState(null);
	const [submitting, setSubmitting] = useState(false);

	const { trigger: createCrew } = useCreateCrew();
	const { trigger: updateCrewName } = useUpdateCrewName();
	const { trigger: deleteCrew } = useDeleteCrew();

	const { isMobile } = useAppSizeId();

	const combobox = useCombobox({
		onDropdownClose: () => combobox.resetSelectedOption(),
	});

	useEffect(() => {
		(async () => {
			await crewStore.fetchMyCrews();

			if (!crewStore.hasCrews()) {
				navigate(routesList.onboardingGroup.path);
			}
		})();
	}, []);

	async function handleSubmitCrew(crew) {
		setSubmitting(true);

		if (crew.id) {
			const apiResponse = await updateCrewName({
				crewId: crew.id,
				payload: {
					...crew,
				},
			});

			handleApiResponse({
				id: "updateCrewName",
				apiResponse,
				successTitle: t`Espacio renombrado con éxito`,
				onSuccess: () => {
					crewStore.fetchMyCrews();
					setEditingCrew(null);
				},
			});
		} else {
			const apiResponse = await createCrew({
				payload: {
					...crew,
				},
			});

			handleApiResponse({
				id: "createCrew",
				apiResponse,
				successTitle: t`Espacio creado con éxito`,
				onSuccess: async ({ response }) => {
					await crewStore.fetchMyCrews();
					crewStore.selectCrewById(response?.id);
					setEditingCrew(null);
				},
			});
		}

		setSubmitting(false);
		crewStore.fetchMyCrews();
		setEditingCrew(null);
	}

	async function handleDeleteCrew(crew) {
		setSubmitting(true);

		const apiResponse = await deleteCrew({
			crewId: crew?.id,
		});

		setSubmitting(false);

		handleApiResponse({
			id: "deleteCrew",
			apiResponse,
			successTitle: t`Espacio eliminado con éxito`,
			onSuccess: async () => {
				await crewStore.fetchMyCrews();
				crewStore.selectCrewByIndex(0);
				setEditingCrew(null);
			},
		});
	}

	const handleEditCrew = useCallback(() => {
		setEditingCrew(crewStore.selected);
	}, []);

	const handleSubmit = useCallback((crew) => handleSubmitCrew(crew), [handleSubmitCrew]);

	const handleDelete = useCallback((crew) => handleDeleteCrew(crew), [handleDeleteCrew]);

	const crewList = crewStore.list ?? [];
	const hasCrews = Boolean(crewList.length);

	// Selector option
	const selectedCrew = crewList.find((_crew) => _crew.id === crewStore.getSelectedCrew("id"));

	return (
		<Stack gap="sm" w="100%">
			{variant === "desktop" && (
				<Group justify="space-between">
					<Title order={4}>
						<Trans>Espacios</Trans>
					</Title>
					<Group gap="xs">
						<UsageLimit feature="crew.maxItems" />
						<ActionIcon
							variant="subtle"
							onClick={() => setEditingCrew({ id: null })}
							id="crew-create-button"
							data-testid="crew-create-btn"
						>
							<IconPlus />
						</ActionIcon>
					</Group>
				</Group>
			)}

			{!hasCrews && (
				<Button
					leftSection={<IconPlus />}
					onClick={() => setEditingCrew({ id: null })}
					id="crew-create-as-new-user-button"
					data-testid="crew-create-empty-btn"
				>
					<Trans>Crear espacio</Trans>
				</Button>
			)}

			{hasCrews && (
				<Group wrap="nowrap" justify="space-between">
					<Combobox
						store={combobox}
						withinPortal={false}
						onOptionSubmit={(_crewId) => {
							crewStore.selectCrewById(_crewId);
							combobox.closeDropdown();

							if (isMobile) {
								uiStore.closeNavbar();
							}
						}}
					>
						<Combobox.Target targetType="button">
							<InputBase
								w={variant === "desktop" ? "100%" : "12rem"}
								component="button"
								pointer
								rightSection={<Combobox.Chevron />}
								onClick={() => combobox.toggleDropdown()}
								rightSectionPointerEvents="none"
								multiline
							>
								{selectedCrew ? (
									selectedCrew?.name
								) : (
									<Input.Placeholder>ERR</Input.Placeholder>
								)}
							</InputBase>
						</Combobox.Target>

						<Combobox.Dropdown>
							<Combobox.Options>
								{crewList.map((item) => {
									const isSelected = item === selectedCrew;

									return (
										<Combobox.Option
											value={item.id}
											key={item.id}
											active={isSelected}
										>
											<Group gap="xs" wrap="nowrap">
												{isSelected && <CheckIcon size={12} />}
												<span>{item.name}</span>
											</Group>
										</Combobox.Option>
									);
								})}
							</Combobox.Options>
						</Combobox.Dropdown>
					</Combobox>

					{variant === "desktop" && (
						<ActionIcon
							className={classes.noGrow}
							variant="subtle"
							onClick={handleEditCrew}
							data-testid="crew-edit-btn"
						>
							<IconPencil />
						</ActionIcon>
					)}
				</Group>
			)}

			{/* ********************************************* */}
			{/* Modals */}
			{/* ********************************************* */}

			<CrewFields
				opened={Boolean(editingCrew)}
				crew={editingCrew}
				onClose={() => setEditingCrew(null)}
				onSubmit={handleSubmit}
				onDelete={handleDelete}
				loading={submitting}
			/>
		</Stack>
	);
});
